import { Flex } from "grid-styled";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { themeGet } from "styled-system";

import { useLabels } from "../../context_providers/LabelContext";
import { withViewer } from "../../context_providers/ViewerContext";

import { setAiSettings } from "./AiSettingsMutation";

import { environment } from "pstat-anywhere/relay";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import Checkbox, { CheckboxGroup } from "pstat-design-system/inputs/Checkbox";
import {
  createErrorMessage,
  createSuccessMessage
} from "pstat-design-system/message/manageMessages";
import Panel from "pstat-design-system/Panel";
import { Button } from "pstat-design-system/shared/Button";
import { H1 } from "pstat-design-system/text/headings";



const PageContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const ButtonContainer = styled(Flex)`
  margin-top: 32px;
  gap: 12px;
  text-align: right;
  justify-content: flex-end;
  background-color: ${themeGet("colors.nav.90")}; // #f3f4f5
  padding: 16px;
  border-radius: 0 0 6px 6px;
`;

const AccessDeniedContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const AccessDeniedTitle = styled.h2`
  font-family: ${themeGet("fontFamily")};
  font-size: 24px;
  font-weight: 500;
  color: ${themeGet("colors.nav.0")};
  margin-bottom: 16px;
`;

const AccessDeniedMessage = styled.p`
  font-family: ${themeGet("fontFamily")};
  font-size: 16px;
  color: ${themeGet("colors.nav.55")};
  margin-bottom: 24px;
`;

const AiSettingsPage = ({ viewer, currentTenant, router, ...otherProps }) => {
  const [aiEnabled, setAiEnabled] = useState(
    currentTenant?.customerSettings?.enableAi || false
  );
  const { t } = useTranslation();
  const { documentLabelPlural } = useLabels();
  const hasAccess = () => {
    if (!viewer) return false;

    return true;
  };

  useEffect(() => {
    setAiEnabled(currentTenant?.customerSettings?.enableAi || false);
  }, [currentTenant]);

  const saveAiSettings = () => {
    setAiSettings(environment, aiEnabled, (response, errors) => {
      if (errors) {
        createErrorMessage(
          t("administration.configuration.aiSettings.errorMessage")
        );
        return;
      }
      if (response.setAiSettings.ok) {
        createSuccessMessage(
          t("administration.configuration.aiSettings.successMessage")
        );
        router.push(`${getRootUrl()}/admin`);
      } else {
        createErrorMessage(
          t("administration.configuration.aiSettings.errorMessage")
        );
        return;
      }
    });
  };

  const handleCheckboxChange = value => {
    setAiEnabled(value);
  };

  const handleCancel = () => {
    router.push(`${getRootUrl()}/admin`);
  };

  // Show access denied if user doesn't have permission
  if (!hasAccess()) {
    return (
      <AccessDeniedContainer>
        <AccessDeniedTitle>Access Denied</AccessDeniedTitle>
        <AccessDeniedMessage>
          You don't have permission to access AI Settings. This page is only
          available to superusers and super-site-admins.
        </AccessDeniedMessage>
      </AccessDeniedContainer>
    );
  }

  return (
    <PageContainer>
      <H1>{t("administration.configuration.aiSettingsHeading")}</H1>
      <div style={{ marginTop: "15px" }}>
        <Panel name={t("administration.configuration.aiSettingsHeaderText")}>
          <Flex alignItems="center">
            <CheckboxGroup
              name="ai-enabled"
              onChange={(_, event) =>
                handleCheckboxChange(event.target.checked)
              }
              selectedValues={aiEnabled ? [aiEnabled] : []}
            >
              <Checkbox
                value={aiEnabled}
                label={t("administration.configuration.aiSettingsDescription", {
                  documentLabelPlural
                })}
              />
            </CheckboxGroup>
          </Flex>
          <ButtonContainer>
            <Button buttonStyle="tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button buttonStyle="secondary" onClick={saveAiSettings}>
              &nbsp;&nbsp;Save&nbsp;&nbsp;
            </Button>
          </ButtonContainer>
        </Panel>
      </div>
    </PageContainer>
  );
};

export default withViewer(AiSettingsPage);
